<template>
  <div>
    <wa-modal-frame
      title="Icon Detail Modal"
      component-class="icon-detail"
      :is-showing="isShowing && !isShowingUpsellModal"
      @close-modal="closeModal()"
    >
      <!-- needed to override title slot from rendering w/ unneccessary title -->
      <span slot="title" />

      <template slot="content">
        <template v-if="isShowingUpsellModal == false">
          <div
            v-if="hasIcon"
            :id="`icon-detail-${icon.label}`"
            class="wrap-icon-detail"
          >
            <div class="icon-detail card roomy margin-bottom-0 with-close text-left">
              <template v-if="display == 'detail'">
                <div class="row roomy laptop:align-stretch laptop:align-between margin-bottom-lg">
                  <div class="column-12 laptop:column">
                    <icon-name-and-link
                      :default-styles="defaultStyles"
                      :icon="isCustomIcon ? customIcon : icon"
                      :is-pro-user="isProUser"
                      :is-pro-icon="isProIcon"
                      :is-using-modal="true"
                      :styling="styling"
                      @openProIconModal="openProIconModal"
                    />
                  </div>

                  <div
                    class="column-12 laptop:column display-flex flex-row flex-nowrap flex-items-center laptop:flex-content-end"
                    style="gap: var(--spacing-md)"
                  >
                    <icon-unicodes
                      :icon="isCustomIcon ? customIcon : icon"
                      :is-duotone="isDuotone"
                      :is-using-modal="true"
                      :styling="styling"
                    />

                    <icon-glyphs
                      :icon="isCustomIcon ? customIcon : icon"
                      :is-duotone="isDuotone"
                      :is-custom-icon="isCustomIcon"
                      :is-using-modal="true"
                      :styling="styling"
                    />

                    <icon-svg-download
                      :can-access-icon="canAccessIcon"
                      :can-download-icon="canDownloadIcon"
                      :icon="isCustomIcon ? customIcon : icon"
                      :is-pro-icon="isProIcon"
                      :is-pro-user="isProUser"
                      :is-custom-icon="isCustomIcon"
                      :is-using-modal="true"
                      :styling="styling"
                      @openProIconModal="openProIconModal"
                      @getSelectedAction="getSelectedAction"
                    />
                  </div>
                </div>

                <!-- <div class="row roomy align-stretch margin-bottom-2xl">
                  <div class="column-12 laptop:column">
                    <wa-monetization-ad />
                  </div>
                </div> -->

                <div class="wrap-icon-details row roomy align-stretch laptop:margin-bottom-2xl">
                  <div class="icon-detail-grid-preview column-12 margin-bottom-2xl laptop:margin-bottom-0 laptop:column-6">
                    <icon-preview
                      :default-styles="defaultStyles"
                      :icon="isCustomIcon ? customIcon : icon"
                      :is-custom-icon="isCustomIcon"
                      :is-duotone="isDuotone"
                      :preview-mode="previewMode"
                      :styling="styling"
                      :version="version"
                      :display-icon-preview-controls="true"
                      @resetStyles="$emit('resetStyles')"
                      @changePreviewMode="(mode) => (previewMode = mode)"
                      @toggleStyleOptions="toggleStyleOptions"
                      @toggleAnimationOptions="toggleAnimationOptions"
                    />
                  </div>

                  <div class="icon-detail-use column-12 laptop:column-6 margin-bottom-2xl laptop:margin-bottom-0">
                    <div class="margin-bottom-2xl">
                      <icon-pack-selector
                        v-if="!isCustomIcon"
                        :icon="icon"
                        :styling="styling"
                        :version="version"
                        :default-styles="defaultStyles"
                        @change="(newStyling) => $emit('stylingChange', newStyling)"
                      />
                    </div>

                    <icon-code-snippet
                      :id="icon.name"
                      :can-access-icon="canAccessIcon"
                      :can-download-icon="canDownloadIcon"
                      :default-styles="defaultStyles"
                      :icon="isCustomIcon ? customIcon : icon"
                      :icon-abstract="iconAbstract"
                      :is-custom-icon="isCustomIcon"
                      :is-duotone="isDuotone"
                      :is-pro-icon="isProIcon"
                      :is-pro-user="isProUser"
                      :is-using-modal="true"
                      :kit-version="kitVersion"
                      :showing="showing"
                      :styling="styling"
                      :viggo="viggo"
                      @show="changeShowing"
                      @openProIconModal="openProIconModal"
                      @getSelectedAction="getSelectedAction"
                    />

                    <icon-actions
                      v-if="!isTemporarilyDisabled"
                      :can-access-icon="canAccessIcon"
                      :can-use-icon="canUseIcon"
                      :icon="isCustomIcon ? customIcon : icon"
                      :is-custom-icon="isCustomIcon"
                      :is-pro-icon="isProIcon"
                      :is-pro-plus-icon="isProPlusIcon(icon)"
                      :is-pro-user="isProUser"
                      :is-using-modal="true"
                      :styling="styling"
                      @editIcon="display = 'edit'"
                      @getSelectedAction="getSelectedAction"
                      @openProIconModal="openProIconModal"
                      @showConfirmDelete="showConfirmDelete"
                    />

                    <confirm-delete
                      v-if="isDeleting"
                      :delete-inprocess="deleteInprocess"
                      @hideConfirmDelete="hideConfirmDelete()"
                      @deleteIcon="deleteIcon"
                    />
                  </div>

                  <div class="icon-detail-styling-tabs column-12">
                    <icon-styling
                      v-show="isShowingStyleOptions"
                      :default-styles="defaultStyles"
                      :icon="isCustomIcon ? customIcon : icon"
                      :styling="styling"
                      :version="version"
                      @change="(newStyling) => $emit('stylingChange', newStyling)"
                    />

                    <icon-animations
                      v-show="isShowingAnimationOptions"
                      :icon="isCustomIcon ? customIcon : icon"
                      :styling="styling"
                      :version="version"
                      @change="(newStyling) => $emit('stylingChange', newStyling)"
                    />
                  </div>
                </div>

                <icon-brand-notice v-if="isBrandsIcon" />

                <icon-sponsorship
                  v-if="isSponsored"
                  :sponsor-name="icon.sponsored.label"
                  :sponsor-url="icon.sponsored.url"
                />

                <div class="row roomy align-middle align-center margin-top-md">
                  <div
                    class="display-flex"
                    style="gap: var(--spacing-md); flex-wrap: wrap; justify-content: center"
                  >
                    <icon-categories
                      v-if="hasCategories"
                      :categories="icon.categories"
                      :icon="icon"
                      :is-using-modal="true"
                      @closeModal="closeModal"
                    />
                    <icon-aliases
                      v-if="hasAliases"
                      :aliases="icon.aliases.names"
                    />

                    <icon-version-info
                      :icon="isCustomIcon ? customIcon : icon"
                      :is-custom-icon="isCustomIcon"
                    />
                  </div>
                </div>
              </template>

              <div v-if="display == 'edit'">
                <icon-customize
                  :default-styles="defaultStyles"
                  :icon="icon"
                  :is-pro-user="isProUser"
                  :is-using-modal="true"
                  :selected-family="icon.family"
                  :selected-style="icon.style"
                  :styling="styling"
                  :version="version"
                  @closeEditor="closeEditor"
                  @change="(newStyling) => $emit('stylingChange', newStyling)"
                  @getSelectedAction="getSelectedAction"
                  @openProIconModal="openProIconModal"
                />
              </div>

              <div>
                <button
                  class="display-none tablet:display-inline-block flat"
                  style="
                    --button-color: var(--fa-gravy);
                    position: absolute;
                    top: var(--icon-details-preview-arrows-top);
                    left: calc(-1 * var(--spacing-7xl));
                    --button-background: transparent;
                  "
                  @click.prevent="prevIcon"
                >
                  <font-awesome-icon
                    :icon="$v6.regular.faAngleLeft"
                    size="4x"
                  />
                  <span class="sr-only">Previous Icon</span>
                </button>

                <button
                  class="display-none tablet:display-inline-block flat"
                  style="
                    --button-color: var(--fa-gravy);
                    position: absolute;
                    top: var(--icon-details-preview-arrows-top);
                    right: calc(-1 * var(--spacing-7xl));
                    --button-background: transparent;
                  "
                  @click.prevent="nextIcon"
                >
                  <font-awesome-icon
                    :icon="$v6.regular.faAngleRight"
                    size="4x"
                  />
                  <span class="sr-only">Next Icon</span>
                </button>
              </div>
            </div>
          </div>
        </template>
      </template>
    </wa-modal-frame>

    <wa-modal-upsell
      v-if="!canAccessIcon"
      :icon="isCustomIcon ? customIcon : icon"
      :is-custom-icon="isCustomIcon"
      :is-showing="isShowingUpsellModal"
      :upsell-requirement="upsellRequirement"
      :upsell-trigger="getSelectedAction(selectedAction)"
      :upsell-trigger-icon-name="selectedActionIcon"
      @close-modal="closeProIconModal"
    />
  </div>
</template>

<script>
  import { EventTrackingMixin, hasAccessToProForActiveVersion, SubscriptionMixin } from 'app/mixins'
  import { get, merge, isEmpty, isEqual } from 'lodash'
  import { IconsMixins } from 'app/views/icons/components/icons/mixins'
  import { mapActions, mapGetters } from 'vuex'
  import { ModalFrameMixin } from 'app/components-wa/modals/ModalFrame'
  import { toggleValueInclusion } from 'app/views/search/mixins'

  import algoliasearch from 'algoliasearch/lite'
  import ConfirmDelete from './ConfirmDelete'
  import IconActions from './IconActions'
  import IconAliases from './IconAliases'
  import IconAnimations from './IconAnimations'
  import IconBrandNotice from './IconBrandNotice'
  import IconCategories from './IconCategories'
  import IconCodeSnippet from './IconCodeSnippet'
  import IconCustomize from '../modifiers/IconCustomize'
  import IconGlyphs from './IconGlyphs'
  import IconNameAndLink from './IconNameAndLink'
  import IconPackSelector from './IconPackSelector'
  import IconPreview from './IconPreview'
  import IconSponsorship from './IconSponsorship'
  import IconStyling from './IconStyling'
  import IconSvgDownload from './IconSvgDownload'
  import IconUnicodes from './IconUnicodes'
  import IconVersionInfo from './IconVersionInfo'

  const ICON_PLACEHOLDER = {
    membership: { free: [], pro: [] },
    family: null,
    style: null,
    aliases: {},
    styles: [],
    categories: [],
    sponsored: {},
    changes: [],
    icon_packs_list: []
  }

  export default {
    name: 'ModalIconDetail',

    components: {
      ConfirmDelete,
      IconActions,
      IconAliases,
      IconAnimations,
      IconBrandNotice,
      IconCategories,
      IconCodeSnippet,
      IconCustomize,
      IconGlyphs,
      IconNameAndLink,
      IconPackSelector,
      IconPreview,
      IconSponsorship,
      IconStyling,
      IconSvgDownload,
      IconUnicodes,
      IconVersionInfo
    },

    mixins: [EventTrackingMixin, hasAccessToProForActiveVersion, IconsMixins, ModalFrameMixin, SubscriptionMixin, toggleValueInclusion],

    props: {
      customIcon: {
        type: Object
      },

      defaultStyles: {
        type: Object
      },

      hasNext: {
        type: Boolean,
        default: true
      },

      hasPrevious: {
        type: Boolean,
        default: true
      },

      iconAbstract: {
        type: Object
      },

      iconName: {
        type: String
      },

      index: {
        type: String
      },

      isTemporarilyDisabled: {
        type: Boolean,
        default: false
      },

      isCustomIcon: {
        type: Boolean,
        default: false
      },

      isDuotone: {
        type: Boolean,
        required: true
      },

      isProUser: {
        type: Boolean
      },

      kitToken: {
        type: String
      },

      kitVersion: {
        type: Number
      },

      selectedFromSearch: {
        type: Object
      },

      styling: {
        type: Object
      },

      viggo: {
        validator(value) {
          return !!value && !!value.SVG
        }
      }
    },

    data() {
      const as = algoliasearch(FONTAWESOME.ALGOLIA_APPLICATION_ID, FONTAWESOME.ALGOLIA_SEARCH_API_KEY)

      const searchClient = as.initIndex(this.index)

      return {
        deleteInprocess: false,
        display: 'detail',
        icon: ICON_PLACEHOLDER,
        isDeleting: false,
        isResettingModalState: false,
        previewMode: 'day',
        searchClient,
        showing: 'html',
        isShowingStyleOptions: false,
        isShowingUpsellModal: false,
        isShowingAnimationOptions: false,
        selectedAction: 'Whoopsie! That action'
      }
    },

    computed: {
      ...mapGetters('plans', ['iconIsIncludedInPlan', 'isProPlusIcon', 'canAccessIconSvg']),

      isIconInPlan() {
        const planSku = this.$subscription ? this.$subscription.plan.sku : 'FA-FREE'

        return this.iconIsIncludedInPlan(this.icon, planSku)
      },

      earlyBackerWithoutSubscription() {
        return this.$dt.isBacker && this.$subscription === null
      },

      earlyBackerHasIconAccess() {
        return this.earlyBackerWithoutSubscription && this.hasAccessToProForActiveVersion
      },

      canDownloadIcon() {
        if (this.earlyBackerHasIconAccess) return true

        if (this.isCustomIcon && this.$permits.has('accessToProDownload')) return true

        if (!this.$dt.active && !this.icon.is_free) return false

        if (!this.isIconInPlan) return false

        const accessToSvg = this.canAccessIconSvg(this.icon, this.$subscription?.plan, this.$permits.has('accessToProDownload'))
        return accessToSvg
      },

      canUseIcon() {
        if (this.earlyBackerHasIconAccess) return true

        if (this.isCustomIcon) return true
        if (this.icon.is_free) return true

        const activeProSubscription = this.$dt.active && this.$subscription.plan.sku !== 'FA-FREE'

        return this.isIconInPlan && activeProSubscription
      },

      canAccessIcon() {
        if (this.isCustomIcon && !this.$permits.has('accessToProDownload')) return false
        if (this.isCustomIcon && this.$permits.has('accessToProDownload')) return true

        return this.canDownloadIcon && this.canUseIcon
      },

      hasIcon() {
        return !!this.styling?.family
      },

      hasAliases() {
        return this.icon?.aliases?.names?.length > 0
      },

      hasCategories() {
        return this.icon?.categories?.length > 0
      },

      isBrandsIcon() {
        return this.icon.family === 'brands'
      },

      isProIcon() {
        return !this.icon.is_free
      },

      isSponsored() {
        return this.icon.sponsored.label && this.icon.sponsored.label.length > 0
      },

      objectID() {
        const family = get(this, ['styling', 'family'], '')
        const style = get(this, ['styling', 'style'], '')

        return `i:${family}:${style}:${this.iconName}`
      },

      selectedActionIcon() {
        if (this.selectedAction.toLowerCase().includes('DOWNLOADING')) {
          return `faArrowDownToLine`
        }

        return `faIcons`
      },

      upsellRequirement() {
        return this.isProPlusIcon(this.icon) ? 'Pro+ Plan' : 'Pro Plan'
      },

      userHasActiveProSubscription() {
        return this.isProUser && this.$permits.has('accessToProDownload')
      },

      version() {
        return this.isCustomIcon ? this.kitVersion : this.$releases.active.versionParts.major
      }
    },

    watch: {
      icon(newIcon, oldIcon) {
        if (this.hasIconChanged(newIcon, oldIcon)) {
          this.trackIcon(newIcon)
        }
      },

      customIcon(newIcon, oldIcon) {
        if (this.hasIconChanged(newIcon, oldIcon)) {
          this.trackIcon(newIcon)
        }
      },

      selectedFromSearch(newIcon) {
        this.closeProIconModal((this.isResettingModalState = true))

        if (!newIcon) {
          this.setIcon(ICON_PLACEHOLDER)
        } else {
          this.setIcon(newIcon)
        }
      },

      async styling(newStyling) {
        if (!newStyling.family) {
          this.setIcon(ICON_PLACEHOLDER)
        }

        if (newStyling.family !== this.icon.family || newStyling.style !== this.icon.style) {
          if (!this.isCustomIcon) {
            const result = await this.searchClient.search('', { facetFilters: this.facetFilters() })

            if (result.hits.length > 0) {
              this.setIcon(result.hits[0])
            } else {
              // This should only happen during development when the Algolia index doesn't have everything for all family styles
              this.setIcon(ICON_PLACEHOLDER)
            }
          }
        }
      }
    },

    methods: {
      ...mapActions('kits', ['fetchKit']),
      ...mapActions('iconUploads', ['deleteIconUpload']),

      getSelectedAction(action) {
        this.selectedAction = action
        return this.selectedAction
      },

      hasIconChanged(newIcon, oldIcon) {
        const newName = get(newIcon, 'name', '')
        const oldName = get(oldIcon, 'name', '')
        const newFamily = get(newIcon, 'family', '')
        const oldFamily = get(oldIcon, 'family', '')

        return !isEmpty(newName) && (!isEqual(newFamily, oldFamily) || !isEqual(newName, oldName))
      },

      hasPlanAccess(sku) {
        return this.iconIsIncludedInPlan(this.icon, sku)
      },

      trackIcon(icon) {
        this.$posthog.capture(
          'icon_view',
          merge(
            {
              context: 'icon_modal',
              icon_name: icon.name,
              icon_style: this.styling.style,
              icon_family: this.styling.family
            },
            this.determinationsEventProps
          )
        )
      },

      setIcon(icon) {
        this.icon = icon

        if (this.showing === 'svg' && !this.canAccessIcon) {
          this.showing = 'html'
        }
      },

      facetFilters() {
        return [
          [
            // search for the objectID OR alias name
            `objectID:${this.objectID}`,
            `objectID:i:brands:${this.iconName}`,
            `aliases.names:${this.iconName}`
          ]
        ]
      },

      changeShowing(snippet) {
        if (snippet === 'svg' && !this.canAccessIcon) {
          this.selectedAction = `Downloading this Icon`
          return this.openProIconModal()
        } else {
          this.showing = snippet
        }
      },

      openProIconModal() {
        const skipModal = this.$posthog.getFeatureFlag('skip-pro-modal-go-to-plans-pro') === 'skip-modal'

        this.purchaseFlowEvent('upsell_click')

        if (skipModal) {
          this.$router.push({ name: 'plans.pro' })
        } else {
          this.purchaseFlowEvent('pro_icon_modal_view')
          this.isShowingUpsellModal = true
        }
      },

      closeProIconModal(isResettingModalState = false) {
        this.isShowingUpsellModal = false

        if (!isResettingModalState) {
          this.purchaseFlowEvent('pro_upsell_modal_cancel')
        }
      },

      closeEditor() {
        this.display = 'detail'
      },

      closeModal() {
        this.closeEditor()
        this.$emit('closeIcon')
      },

      prevIcon() {
        if (this.hasPrevious) {
          this.$posthog.capture('icon_modal_previous_button_click')
          this.closeEditor()
          this.$emit('prevIcon')
        }
      },

      nextIcon() {
        if (this.hasNext) {
          this.$posthog.capture('icon_modal_next_button_click')
          this.closeEditor()
          this.$emit('nextIcon')
        }
      },

      showConfirmDelete() {
        this.isDeleting = true
      },

      hideConfirmDelete() {
        this.isDeleting = false
      },

      toggleStyleOptions() {
        this.isShowingAnimationOptions = false
        this.isShowingStyleOptions = !this.isShowingStyleOptions
      },

      toggleAnimationOptions() {
        this.isShowingStyleOptions = false
        this.isShowingAnimationOptions = !this.isShowingAnimationOptions
      },

      async deleteIcon() {
        this.deleteInprocess = true

        try {
          const iconName = this.iconAbstract.icon.name
          await this.deleteIconUpload({ kitToken: this.kitToken, id: this.iconAbstract.icon.id })

          this.isDeleting = false
          this.$emit('iconDeleted', iconName)
        } catch (error) {
          throw new Error(error)
        } finally {
          this.deleteInprocess = false
        }
      }
    }
  }
</script>
